import React, { ChangeEvent, useState } from 'react';
import ToggleButton from '../buttons/ToggleButton';
import clsx from 'clsx';
import PromptModal from '../modals/PromptModal';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import History from '../History';
import { FAV_TAG, Prompt } from '../../../../../api/responsePayload/PromptResponse';
import { NODE_DISPLAY_NAMES } from '../../PromptManager';

import styles from "./PromptCard.module.css"

interface PromptCardProps {
    prompt: Prompt;
    onCreate?: (
        newNodeName: string,
        newPromptValue: string
    ) => void
    resetToDefault: (nodeName: string) => void;
    instruction?: string;
    setRefresh: (value: boolean) => void;
    tempUpdate: (value: number) => void;
    setFav: (value: boolean) => void;
    setDefault: (value: boolean) => void;
}

const PromptCard: React.FC<PromptCardProps> = ({ prompt, instruction, onCreate, resetToDefault, setRefresh, tempUpdate, setFav, setDefault }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showDialogConfirm, setShowDialogConfirm] = useState(false);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [showCreateNewDialogConfirm, setShowCreateNewDialogConfirm] = useState(false);
    const [promptValue, setPromptValue] = useState(prompt.prompt);
    const [tempValue, setTempValue] = useState(prompt.node.temperature);

    const handleDefaultButton = () => {
        setShowDialogConfirm(prev => !prev)
    };

    const handleHistoryButton = () => {
        setShowHistoryDialog(prev => !prev)
    };

    const handleEditButton = () => {
        setIsModalOpen((prev) => !prev);
    };

    const handleShowConfirmDialog = () => {
        setShowDialogConfirm(prev => !prev)
        resetToDefault(prompt.node.node_name)
    }

    const handleUpdatePrompt = () => {
        setShowCreateNewDialogConfirm(true)
    }

    const handleCreateNewConfirmDialogButton = async () => {
        setShowCreateNewDialogConfirm(false)
        onCreate && onCreate(prompt.node.node_name, promptValue)
        handleEditButton()
    }

    const handleTempChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        console.log("Temp:", value);
        try {
            const float = parseFloat(value)
            setTempValue(float)
        } catch (e: any) {
            console.log(e)
            alert("Invalid Value");
        }
    }

    const handleInputBlur = (e: ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        console.log(e);
        if (tempValue >= 0 && tempValue <= 1) {
            tempUpdate(tempValue);
            alert(`Temp Set to ${tempValue}`)
        } else {
            alert("Temp must be between 0-1")
        }
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={clsx(styles.title)}>{NODE_DISPLAY_NAMES[prompt.node.node_name] || prompt.node.node_name}</div>
                    <div className={styles.buttonsContainer}>
                        <input
                            className={styles.tempEditor}
                            type='number'
                            step={0.1}
                            onBlur={handleInputBlur}
                            onChange={handleTempChange}
                            value={tempValue} />
                        <ToggleButton
                            style={{ color: "#e74c3c" }}
                            toolTipDirection='top'
                            data-tooltip={prompt.tag === FAV_TAG ? "Remove from Favourite" : "Add to Favourite"}
                            isOn={prompt.tag === FAV_TAG}
                            onValueChange={setFav} />
                        <ToggleButton
                            style={{ color: "#e17100" }}
                            toolTipDirection='top'
                            icon='bookmark'
                            data-tooltip={prompt.is_default ? "Default Prompt" : "Make this Default"}
                            isOn={prompt.is_default}
                            onValueChange={setDefault} />
                        <ToggleButton
                            icon='arrow_back'
                            style={{ color: "var(--color05-dark)" }}
                            toolTipDirection='top'
                            data-tooltip={"Revert to Default"}
                            isOn={prompt.is_default}
                            onClick={handleDefaultButton} />
                        <ToggleButton
                            icon='history'
                            style={{ color: "oklch(44.6% 0.043 257.281)" }}
                            toolTipDirection='top'
                            data-tooltip={"View History"}
                            isOn={prompt.is_default}
                            onClick={handleHistoryButton} />
                        <ToggleButton
                            icon='edit'
                            style={{ color: "var(--send-bg)" }}
                            toolTipDirection='top'
                            data-tooltip={"Edit Prompt"}
                            isOn={prompt.is_default}
                            onClick={handleEditButton} />
                        {/* <button className={clsx(styles.button, styles.primary)} onClick={handleEditButton}>Edit</button> */}
                    </div>
                </div>
                <div className={styles.content}>
                    <pre className={styles.codeBlock}>
                        {prompt.prompt}
                    </pre>
                </div>
            </div>
            <PromptModal
                isOpen={isModalOpen}
                onClose={() => {
                    setPromptValue(prompt.prompt)
                    setIsModalOpen(false)
                }}
                onSave={handleUpdatePrompt}
                title={`Edit: ${NODE_DISPLAY_NAMES[prompt.node.node_name] || prompt.node.node_name}`}
                instruction={instruction}>
                <textarea
                    onChange={e => setPromptValue(e.target.value)}
                    value={promptValue}
                    className={styles.input}
                    rows={20}
                />
            </PromptModal>
            <PromptModal
                isOpen={showHistoryDialog}
                onClose={() => {
                    setRefresh(true);
                    setShowHistoryDialog(false)
                }}
                onSave={() => { }}
                isAction={false}
                title={`${NODE_DISPLAY_NAMES[prompt.node.node_name] || prompt.node.node_name} - Node History`} >
                <History nodeName={prompt.node.node_name} />
            </PromptModal>
            <ConfirmationDialog
                title="Are you sure you want to set the prompt to default?"
                isOpen={showDialogConfirm}
                onCancel={() => { setShowDialogConfirm(false) }}
                onConfirm={handleShowConfirmDialog}
            />
            <ConfirmationDialog
                isOpen={showCreateNewDialogConfirm}
                title='Are you sure you want to update this prompt?'
                onCancel={() => {
                    setShowCreateNewDialogConfirm(false)
                }}
                onConfirm={handleCreateNewConfirmDialogButton} />
        </>
    );
};

export default PromptCard;